import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Eye, Pencil, Trash2, Search, SortAsc } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Loading from "../../component/Loading/Loading";
import AlertMsg from "../../component/AlertMsg/AlertMsg";
import Pagination from "../../utils/Pagenation/Pagenation";
import useAxiosInstance from "../../services/useAxiosInstance";
import GlassmorphicModal from "../../component/ReusableComponents/GlassmorphicModal";
import { AiOutlineSortAscending } from "react-icons/ai";

const ContactList = () => {
  const axiosInstance = useAxiosInstance();
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContact, setEditContact] = useState({
    contactId: "",
    name: "",
    phoneNumber: "",
    email: "",
    planRange: "",
    planPeriod: "",
    planType: "",
    activeStatus: "",
    followUpDate: "",
  });

  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [planTypeOptions, setPlanTypeOptions] = useState([]);
  const [planPeriodOptions, setPlanPeriodOptions] = useState([]);
  const [planRangeOptions, setPlanRangeOptions] = useState([]);
  const [selectedPlanType, setSelectedPlanType] = useState("");
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState("");
  const [selectedPlanRange, setSelectedPlanRange] = useState("");
  const [loading, setLoading] = useState(false);
  const communityId = useSelector((state) => state.auth.communityId);
  const userId = useSelector((state) => state.auth.userId);
  const [alert, setAlert] = useState({ visible: false, message: "", type: "" });
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    fetchSelectOptions();
  }, [selectedPlanType, selectedPlanPeriod, selectedPlanRange]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/public/contacts/getAllContactsWithPlanDetails`);
      setContacts(response.data);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectOptions = async () => {
    try {
      const periodResponse = await axiosInstance.get(`/api/public/plan-period/all`);
      setPlanPeriodOptions(
        periodResponse.data.map((period) => ({
          value: period.planPeriod,
        }))
      );

      const rangeResponse = await axiosInstance.get(`/api/public/plan-range/all`);
      setPlanRangeOptions(
        rangeResponse.data.map((range) => ({
          value: range.planRange,
        }))
      );

      const typeResponse = await axiosInstance.get(`/api/public/plan-type/all`);
      setPlanTypeOptions(
        typeResponse.data.map((type) => ({
          value: type.planType,
        }))
      );
    } catch (error) {
      console.error("Error fetching select options:", error);
    }
  };

  const handleChangePlanType = (event) => {
    setSelectedPlanType(event.target.value);
  };

  const handleChangePlanPeriod = (event) => {
    setSelectedPlanPeriod(event.target.value);
  };

  const handleChangePlanRange = (event) => {
    setSelectedPlanRange(event.target.value);
  };

  const handleDelete = async (contactId) => {
    try {
      setLoading(true);
      setShowDeleteModal(false);
      await axiosInstance.delete(`/api/public/contacts/delete/${contactId}`);
      fetchContacts();
      setAlert({
        visible: true,
        message: "Contact deleted successfully!",
        type: "success",
      });
    } catch (error) {
      console.error("Error deleting contact:", error);
      setAlert({
        visible: true,
        message: "Error deleting contact: Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    const newErrors = {};

    // Validation for all fields
    if (!editContact.name.trim()) newErrors.name = "Name field is required";
    if (!editContact.email.trim()) newErrors.email = "Email field is required";
    else if (!/^\S+@\S+\.\S+$/.test(editContact.email))
      newErrors.email = "Invalid email format (example@gmail.com)";
    if (!editContact.phoneNumber) newErrors.phoneNumber = "Phone Number field is required";
    if (!editContact.followUpDate) newErrors.followUpDate = "Date is required";

    setErrors(newErrors);

    // Stop submission if errors exist
    if (Object.keys(newErrors).length > 0) return;

    try {
      setLoading(true);
      setShowEditModal(false);
      await axiosInstance.put(
        `/api/public/contacts/update/${editContact.contactId}`,
        editContact
      );
      fetchContacts();
      setAlert({
        visible: true,
        message: "Contact updated successfully!",
        type: "success",
      });
    } catch (error) {
      setAlert({
        visible: true,
        message: "Error updating contact: Please try again.",
        type: "error",
      });
      console.error("Error updating contact:", error);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const openViewModal = (contact) => {
    setSelectedContact(contact);
    setShowViewModal(true);
  };

  const openEditModal = (contact) => {
    setSelectedContact(contact);
    setEditContact(contact);
    setShowEditModal(true);
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setEditContact((prevContact) => ({
      ...prevContact,
      [name]: name === "activeStatus" ? value === "true" : value,
    }));

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      if (value.trim()) delete newErrors[name];

      // Special case: email regex
      if (name === "email" && /^\S+@\S+\.\S+$/.test(value)) delete newErrors.email;

      return newErrors;
    });
  };

  const handlePhoneChange = (value) => {
    setEditContact((prevContact) => ({
      ...prevContact,
      phoneNumber: value,
    }));
  };

  const clearFilters = () => {
    setSelectedPlanPeriod("");
    setSelectedPlanType("");
    setSelectedPlanRange("");
  };

  const handleSort = () => {
    const sortedRolePages = [...contacts].sort((a, b) => {
      const pageNameA = a.name.toLowerCase();
      const pageNameB = b.name.toLowerCase();

      if (sortOrder === 'asc') {
        return pageNameA.localeCompare(pageNameB);
      } else {
        return pageNameB.localeCompare(pageNameA);
      }
    });

    setContacts(sortedRolePages);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const filteredContacts = (contacts || []).filter((contact) => {
    const nameMatch = contact.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const typeMatch =
      !selectedPlanType || contact.planType === selectedPlanType;
    const periodMatch =
      !selectedPlanPeriod || contact.planPeriod === selectedPlanPeriod;
    const rangeMatch =
      !selectedPlanRange || contact.planRange === selectedPlanRange;

    return nameMatch && typeMatch && periodMatch && rangeMatch;
  });

  const pageCount = Math.ceil(filteredContacts.length / itemsPerPage);

  const filteredData = filteredContacts.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Delete modal footer with action buttons
  const deleteModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
        onClick={() => handleDelete(selectedContact.contactId)}
      >
        Delete
      </button>
    </div>
  );

  // View modal footer with close button
  const viewModalFooter = (
    <div className="flex justify-end">
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={closeModal}
      >
        Close
      </button>
    </div>
  );

  // Edit modal footer with action buttons
  const editModalFooter = (
    <div className="flex justify-end space-x-2">
      <button
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        onClick={closeModal}
      >
        Cancel
      </button>
      <button
        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        onClick={handleEdit}
      >
        Save
      </button>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen md:p-4">
      {loading && <Loading />}
      {!loading && (
        <div className="max-w-7xl mx-auto">
          {alert.visible && (
            <AlertMsg
              message={alert.message}
              type={alert.type}
              onClose={() => setAlert({ ...alert, visible: false })}
            />
          )}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Contact List</h1>
          </div>
          <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
            <div className="p-4 flex flex-col lg:flex-row items-start gap-2 md:gap-4">
              <div className="relative w-full lg:w-80 mb-0 md:mb-4 lg:mb-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Search Contact..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 w-full">
                <select
                  value={selectedPlanRange}
                  onChange={handleChangePlanRange}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Range</option>
                  {planRangeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedPlanPeriod}
                  onChange={handleChangePlanPeriod}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Period</option>
                  {planPeriodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>

                <select
                  value={selectedPlanType}
                  onChange={handleChangePlanType}
                  className="px-3 py-[11px] rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Plan Type</option>
                  {planTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>

                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                  type="button"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-blue-400">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider flex items-center">
                      Name
                      <button className="inline-flex ml-1" onClick={handleSort} aria-label="Sort">
                        <AiOutlineSortAscending className="h-5 w-5" />
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Phone Number</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Email Address</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Range</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Period</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Plan Type</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Follow Up Date</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider whitespace-nowrap">Status</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-white tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-4 text-center text-sm text-gray-500">No Data Found</td>
                    </tr>
                  ) : (
                    filteredData.map((contact, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.phoneNumber}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.planRange}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.planPeriod}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{contact.planType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {contact.followUpDate
                            ? new Date(contact.followUpDate).toLocaleDateString("en-GB")
                            : ""}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${contact.activeStatus
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                            }`}>
                            {contact.activeStatus ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex gap-3">
                            <button
                              onClick={() => openViewModal(contact)}
                              className="text-blue-500 hover:text-blue-700"
                              title="View Contact"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openEditModal(contact)}
                              className="text-gray-500 hover:text-gray-700"
                              title="Edit Contact"
                            >
                              <Pencil size={18} />
                            </button>
                            <button
                              onClick={() => openDeleteModal(contact)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete Contact"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {currentPage + 1} of {pageCount}
              </div>
              <div className="flex-1 flex justify-end">
                <Pagination
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                />
              </div>
            </div>
          </div>

          {/* View Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showViewModal}
            onClose={closeModal}
            title="Contact Details"
            size="lg"
            footer={viewModalFooter}
          >
            {selectedContact && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={selectedContact.name}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                  <input
                    type="text"
                    value={selectedContact.phoneNumber}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="text"
                    value={selectedContact.email}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                  <input
                    type="text"
                    value={selectedContact.activeStatus ? "Active" : "Inactive"}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                  <input
                    type="text"
                    value={selectedContact.planType}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Period</label>
                  <input
                    type="text"
                    value={selectedContact.planPeriod}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Range</label>
                  <input
                    type="text"
                    value={selectedContact.planRange}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Follow Up Date</label>
                  <input
                    type="text"
                    value={selectedContact.followUpDate ? new Date(selectedContact.followUpDate).toLocaleDateString("en-GB") : ""}
                    readOnly
                    className="w-full rounded-md border border-gray-300 px-4 py-2 bg-gray-50"
                  />
                </div>
              </div>
            )}
          </GlassmorphicModal>

          {/* Edit Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showEditModal}
            onClose={closeModal}
            title="Edit Contact"
            size="lg"
            footer={editModalFooter}
          >
            {selectedContact && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    maxLength={100}
                    name="name"
                    value={editContact.name}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${errors.name ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    maxLength={100}
                    name="email"
                    value={editContact.email}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${errors.email ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    country={'us'}
                    maxLength={10}
                    value={editContact.phoneNumber}
                    onChange={handlePhoneChange}
                    inputProps={{
                      name: "phoneNumber",
                      required: true,
                      autoFocus: true,
                      className: "w-full rounded-md border px-4 py-2 pl-8 focus:outline-none focus:ring-2 focus:ring-blue-500",
                    }}
                    containerClass="phone-input"
                    inputClass={`${errors.phoneNumber ? "border-red-500" : "border-gray-300"}`}
                    buttonStyle={{ border: "none", background: "transparent" }}
                  />
                  {errors.phoneNumber && <p className="text-red-500 text-xs mt-1">{errors.phoneNumber}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Active Status</label>
                  <div className="flex gap-4 mt-2">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="activeStatus"
                        value="true"
                        checked={editContact.activeStatus === true}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="activeStatus"
                        value="false"
                        checked={editContact.activeStatus === false}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-blue-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">Inactive</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Follow Up Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="followUpDate"
                    value={editContact.followUpDate?.split("T")[0] || ""}
                    onChange={handleInputChange}
                    className={`w-full rounded-md border ${errors.followUpDate ? "border-red-500" : "border-gray-300"} px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {errors.followUpDate && <p className="text-red-500 text-xs mt-1">{errors.followUpDate}</p>}
                </div>
              </div>
            )}
          </GlassmorphicModal>

          {/* Delete Modal using GlassmorphicModal */}
          <GlassmorphicModal
            isOpen={showDeleteModal}
            onClose={closeModal}
            title="Confirm Delete"
            size="sm"
            footer={deleteModalFooter}
          >
            {selectedContact && (
              <p className="text-gray-700">
                Are you sure you want to delete the contact for {selectedContact.name}? This action cannot be undone.
              </p>
            )}
          </GlassmorphicModal>
        </div>
      )}
    </div>
  );
};

export default ContactList;